<script setup lang="ts">
import type { ComputedRef } from 'vue';
import CssIcon from './CssIcon.vue';
import { NuxtLink } from '#components';

interface MenuItem {
  type: 'button' | 'link';
  icon: string;
  text: string | ComputedRef<string>;
  onClick?: () => void;
  to?: string;
  activeCondition?: boolean | ComputedRef<boolean>;
  hasBadge?: boolean;
}

const props = defineProps<{
  button: MenuItem;
  disabled?: boolean;
}>();
</script>

<template>
  <div class="item-wrapper">
    <component
      :is="props.button.type === 'link' ? NuxtLink : 'button'"
      class="btn"
      :class="{ active: props.button.activeCondition }"
      :disabled="props.disabled"
      :no-prefetch="props.button.type === 'link'"
      :to="props.button.to"
      @click="props.button.onClick"
    >
      <span
        v-if="props.button.hasBadge"
        class="badge-live"
      />
      <span class="icon-slot">
        <CssIcon :name="props.button.icon" />
      </span>
      <span class="label">{{ props.button.text }}</span>
    </component>
  </div>
</template>

<style scoped lang="scss">
.item-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  flex: 1;
}

.btn {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: rgba(255, 255, 255, 1);
  min-width: 44px;
  height: 100%;
  transition: color 0.15s ease;

  &.active {
    color: #00c9ff;
  }

  &:disabled {
    opacity: 0.45;
    pointer-events: none;
  }
}

.icon-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.label {
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
}

.badge-live {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ff3400;
  animation: blink 1800ms linear infinite;
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}
</style>
